import { useEffect, useState } from "react";
import { ArrowDownCircle, ArrowUpCircle, History, Search, Download } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, formatRupiah, formatTanggal } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Savings() {
  const { user, schoolId, academicYear, setAcademicYear } = useAuth();
  const canEdit = ["super_admin", "bendahara"].includes(user.role);
  const [rows, setRows] = useState(null);
  const [needsSchool, setNeedsSchool] = useState(false);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("semua");
  const [txTarget, setTxTarget] = useState(null); // {student, type}
  const [txForm, setTxForm] = useState({ amount: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [history, setHistory] = useState(null);
  const [ayears, setAyears] = useState([]);

  useEffect(() => {
    api.get("/academic-years").then((r) => {
      setAyears(r.data.years || []);
      if (!academicYear && r.data.current) setAcademicYear(r.data.current);
    }).catch(() => {});
  }, [schoolId]);

  const load = () => {
    setNeedsSchool(false);
    api.get("/savings")
      .then((r) => setRows(r.data))
      .catch((e) => {
        if (e.response?.status === 400) { setNeedsSchool(true); setRows([]); }
        else { setRows([]); toast.error(apiError(e)); }
      });
  };

  useEffect(() => {
    if (user.role === "super_admin" && !schoolId) { setNeedsSchool(true); setRows([]); return; }
    load();
  }, [schoolId]);

  const openTx = (row, type) => {
    setTxTarget({ student: row.student, type });
    setTxForm({ amount: "", note: "" });
  };

  const handleTx = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/savings/transaction", {
        student_id: txTarget.student.id,
        type: txTarget.type,
        amount: Number(txForm.amount),
        note: txForm.note,
      });
      toast.success(`${txTarget.type === "setor" ? "Setoran" : "Penarikan"} ${txTarget.student.name} tercatat`);
      setTxTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (row) => {
    setHistoryTarget(row.student);
    setHistory(null);
    try {
      const r = await api.get(`/savings/history?student_id=${row.student.id}${academicYear ? `&ayear=${encodeURIComponent(academicYear)}` : ""}`);
      setHistory(r.data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (needsSchool) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="savings-need-school">
        <p className="font-heading text-lg font-semibold text-slate-800">Pilih sekolah terlebih dahulu</p>
        <p className="mt-1 text-sm text-slate-500">Gunakan pemilih sekolah di pojok kiri atas.</p>
      </div>
    );
  }

  const classes = [...new Set((rows || []).map((r) => r.student.class_name))].sort();
  const filtered = (rows || []).filter(
    (r) =>
      (classFilter === "semua" || r.student.class_name === classFilter) &&
      (r.student.name.toLowerCase().includes(search.toLowerCase()) || r.student.nisn.toLowerCase().includes(search.toLowerCase()))
  );
  const totalSaldo = (rows || []).reduce((acc, r) => acc + r.balance, 0);

  return (
    <div className="space-y-6" data-testid="savings-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Tabungan Siswa</h1>
          <p className="mt-1 text-sm text-slate-500">
            Total saldo seluruh siswa: <span className="font-semibold text-slate-700" data-testid="savings-total">{rows === null ? "…" : formatRupiah(totalSaldo)}</span>
          </p>
        </div>
        <Button variant="outline" onClick={async () => {
          try {
            const res = await api.get("/export/savings", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = "rekap_tabungan.xlsx";
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Rekap tabungan berhasil diunduh");
          } catch { toast.error("Gagal mengunduh rekap tabungan"); }
        }} data-testid="savings-export-button" className="gap-2 rounded-md">
          <Download className="h-4 w-4" strokeWidth={1.5} /> Ekspor Excel
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
            <Input placeholder="Cari nama atau NISN..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="savings-search-input" />
          </div>
          <Select value={academicYear} onValueChange={setAcademicYear}>
            <SelectTrigger className="w-[150px]" data-testid="savings-ayear-select"><SelectValue placeholder="Tahun ajaran" /></SelectTrigger>
            <SelectContent>
              {(ayears.length ? ayears : academicYear ? [academicYear] : []).map((y) => (
                <SelectItem key={y} value={y} data-testid={`savings-ayear-option-${y.replace("/", "-")}`}>TA {y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger className="w-[160px]" data-testid="savings-class-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="semua" data-testid="savings-class-semua">Semua Kelas</SelectItem>
              {classes.map((c) => (
                <SelectItem key={c} value={c} data-testid={`savings-class-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          <Table data-testid="savings-table">
            <TableHeader>
              <TableRow>
                <TableHead>NISN</TableHead>
                <TableHead>Nama Siswa</TableHead>
                <TableHead>Kelas</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows === null && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-slate-500">Memuat data...</TableCell></TableRow>
              )}
              {rows !== null && filtered.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-slate-500">Belum ada data siswa.</TableCell></TableRow>
              )}
              {filtered.map((r) => (
                <TableRow key={r.student.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`savings-row-${r.student.nisn}`}>
                  <TableCell className="text-sm text-slate-600">{r.student.nisn}</TableCell>
                  <TableCell className="text-sm font-medium text-slate-800">{r.student.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.student.class_name}</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-slate-900" data-testid={`savings-balance-${r.student.nisn}`}>{formatRupiah(r.balance)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {canEdit && (
                        <>
                          <Button size="sm" onClick={() => openTx(r, "setor")} data-testid={`savings-deposit-${r.student.nisn}`} className="gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98]">
                            <ArrowDownCircle className="h-3.5 w-3.5" strokeWidth={1.5} /> Setor
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openTx(r, "tarik")} data-testid={`savings-withdraw-${r.student.nisn}`} className="gap-1.5 rounded-md text-amber-700 border-amber-200 hover:bg-amber-50">
                            <ArrowUpCircle className="h-3.5 w-3.5" strokeWidth={1.5} /> Tarik
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openHistory(r)} data-testid={`savings-history-${r.student.nisn}`} className="gap-1.5 text-slate-600">
                        <History className="h-3.5 w-3.5" strokeWidth={1.5} /> Riwayat
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!txTarget} onOpenChange={() => setTxTarget(null)}>
        <DialogContent aria-describedby={undefined} data-testid="savings-tx-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {txTarget?.type === "setor" ? "Setor Tabungan" : "Tarik Tabungan"}
            </DialogTitle>
          </DialogHeader>
          {txTarget && (
            <form onSubmit={handleTx} className="space-y-4">
              <div className="rounded-lg bg-slate-50 px-4 py-3">
                <p className="text-sm font-medium text-slate-800">{txTarget.student.name}</p>
                <p className="text-xs text-slate-500">{txTarget.student.class_name} · NISN {txTarget.student.nisn}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tx-amount">Jumlah (Rp)</Label>
                <Input id="tx-amount" type="number" min="1" data-testid="savings-amount-input" value={txForm.amount} onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })} required placeholder="Contoh: 20000" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tx-note">Catatan (opsional)</Label>
                <Input id="tx-note" data-testid="savings-note-input" value={txForm.note} onChange={(e) => setTxForm({ ...txForm, note: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setTxTarget(null)} data-testid="savings-tx-cancel">Batal</Button>
                <Button type="submit" disabled={saving} data-testid="savings-tx-submit" className={txTarget.type === "setor" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}>
                  {saving ? "Menyimpan..." : "Simpan"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyTarget} onOpenChange={() => setHistoryTarget(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl" data-testid="savings-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Riwayat Tabungan — {historyTarget?.name}</DialogTitle>
          </DialogHeader>
          {history && (
            <p className="text-sm text-slate-600">Saldo saat ini: <span className="font-semibold text-slate-900" data-testid="savings-history-balance">{formatRupiah(history.balance)}</span></p>
          )}
          <div className="max-h-[400px] overflow-y-auto">
            <Table data-testid="savings-history-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Jenis</TableHead>
                  <TableHead className="text-right">Jumlah</TableHead>
                  <TableHead className="text-right">Saldo Berjalan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history && history.transactions.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="py-8 text-center text-sm text-slate-500">Belum ada transaksi.</TableCell></TableRow>
                )}
                {(history?.transactions || []).map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="text-sm text-slate-600">{formatTanggal(t.created_at)}</TableCell>
                    <TableCell>
                      {t.type === "setor" ? (
                        <Badge className="rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-50">Setor</Badge>
                      ) : (
                        <Badge className="rounded-full bg-amber-50 text-amber-600 hover:bg-amber-50">Tarik</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm font-medium text-slate-900">{formatRupiah(t.amount)}</TableCell>
                    <TableCell className="text-right text-sm text-slate-600">{formatRupiah(t.balance_after)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
